import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Divider, Drawer, Typography } from '@mui/material';
import accountsJwtService from 'app/services/jwtService';
import PropTypes from 'prop-types';

import { IS_SWITCH_THEME_MODE_ALLOWED } from 'constants/themes';
import { getDefaultErrorMessage } from 'utils/axios';

import { logoutMasterAction } from 'store/auth/actions';
import { getMaster } from 'store/auth/selectors';
import { showErrorMessageAction } from 'store/global/actions';
import { selectNavigationConfig } from 'store/global/selectors';

import { DefaultLayoutTestIds } from '../../DefaultLayout.testIds';
import MasterMenu from '../Header/MasterMenu';
import NavigationMenu from '../Header/NavigationMenu';
import ThemeToggle from '../Header/ThemeToggle';

import {
  backdropStyles,
  contentBlockStyles,
  drawerStyles,
  flatMenuButtonStyles,
  themeRowStyles,
  wrapperStyles,
} from './MenuSidebar.styles';

const backdropProps = {
  sx: backdropStyles,
};

const paperProps = {
  sx: wrapperStyles,
};

const slideProps = {
  timeout: { enter: 300, exit: 300 },
  appear: true,
  tabIndex: 'unset',
};

const MenuSidebar = ({ isSidebarOpen, onClose }) => {
  const dispatch = useDispatch();

  const master = useSelector(getMaster);
  const navigation = useSelector(selectNavigationConfig);

  const { impersonateMode } = master.data;

  // An uncertified sub-company user has every navigable surface filtered out (STP-331), leaving an
  // empty padded block wedged between two dividers.
  const hasNavigation = !!navigation.length;

  const onLogoutClick = useCallback(() => {
    if (!impersonateMode) {
      dispatch(logoutMasterAction({ isExplicitLogout: true }));
      onClose();

      return;
    }

    accountsJwtService.revertImpersonate().catch(err =>
      dispatch(
        showErrorMessageAction({
          message: getDefaultErrorMessage(err),
          err,
        }),
      ),
    );

    onClose();
  }, [dispatch, impersonateMode, onClose]);

  return (
    <Drawer
      BackdropProps={backdropProps}
      PaperProps={paperProps}
      SlideProps={slideProps}
      data-testid={DefaultLayoutTestIds.HEADER_SIDEBAR_MENU}
      open={isSidebarOpen}
      sx={drawerStyles}
      onClose={onClose}
    >
      <Box sx={contentBlockStyles}>
        <MasterMenu isFlat onCloseSidebar={onClose} />
      </Box>

      <Divider />

      {IS_SWITCH_THEME_MODE_ALLOWED && (
        <>
          <Box sx={themeRowStyles}>
            <Typography variant="medium16">Theme</Typography>
            <ThemeToggle onSurface />
          </Box>

          <Divider />
        </>
      )}

      {hasNavigation && (
        <Box sx={contentBlockStyles}>
          <NavigationMenu isVertical onCloseSidebar={onClose} />
        </Box>
      )}

      {/* Pinning Logout to the bottom only reads as deliberate while the nav fills the space above
          it. With every item filtered out (STP-331) the push leaves the drawer ~80% blank, so let
          Logout sit directly under the theme row instead. */}
      <Divider className={hasNavigation ? '!mt-auto' : ''} />

      <Box sx={contentBlockStyles}>
        <Button
          data-testid={DefaultLayoutTestIds.MENU_SIDEBAR_LOGOUT}
          sx={flatMenuButtonStyles}
          onClick={onLogoutClick}
        >
          {impersonateMode ? 'Get back to admin' : 'Logout'}
        </Button>
      </Box>
    </Drawer>
  );
};

MenuSidebar.propTypes = {
  isSidebarOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(MenuSidebar);
